import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import axios from 'axios';

import { navFlags } from '@dbt-labs/app-nav/flags';
import { kebabCaseFeatureFlagSet } from '@dbt-labs/dbt-explorer';
import { setupAmplitudeIntegration } from '@dbt-labs/integrations/amplitude';
import { setupDatadogIntegration } from '@dbt-labs/integrations/datadog';
import {
  asyncFlagProvider,
  FeatureFlagContextProvider,
} from '@dbt-labs/integrations/feature-flags/providers';
import { setupPendoIntegration } from '@dbt-labs/integrations/pendo';
import { setupSnowplowIntegration } from '@dbt-labs/integrations/snowplow';
import { listAccounts } from '@dbt-labs/stable-api/accounts';
import { getConstants } from '@dbt-labs/stable-api/constants';
import {} from '@dbt-labs/stable-api/constants';
import { Account, Constants, Whoami } from '@dbt-labs/stable-api/types';
import { getWhoami } from '@dbt-labs/stable-api/whoami';

import { App } from './App';

import '@dbt-labs/app-nav/dist/axios-config';
import './axios-config';

import './index.css';

const maybeInitializeApp = async (
  constants: Constants,
  whoami?: Whoami,
  accounts?: Account[],
) => {
  const { environment_configuration: envConfig } = constants;
  const {
    datadog_logs_client_token,
    dbt_cloud_version,
    disable_anonymous_tracking,
    env,
    dd_env,
    force_enable_tracking,
    amplitude_key,
    launchdarkly_key_prefix,
  } = envConfig;
  const isProdCluster = env === 'prod';

  if ((isProdCluster && !disable_anonymous_tracking) || force_enable_tracking) {
    if (whoami?.user) {
      setupSnowplowIntegration(whoami.user.id, 'explorer');
      if (env) {
        setupPendoIntegration(whoami.user, launchdarkly_key_prefix ?? '', env);
      }
      // TODO: Do we need to do this?
      // setupGoogleAnalytics(whoami?.user);
    }
    if (amplitude_key) {
      setupAmplitudeIntegration(amplitude_key);
    }
    setupDatadogIntegration(
      'ace50bf6-6bc4-4f4c-be84-35e12e75a0f7',
      'pub5c930d375e7baab6afbbd877bc822765',
      datadog_logs_client_token,
      dd_env,
      dbt_cloud_version,
      'dbt-explorer',
      ['warn', 'error'],
    );
  }

  const kebabNavFlags = Object.fromEntries(
    Object.values(navFlags).map((flag) => [flag, undefined]),
  );

  // Set up LD integration and Feature Flags
  const { LDProvider } = await asyncFlagProvider({
    constants,
    user: whoami?.user,
    accounts,
    flags: { ...kebabCaseFeatureFlagSet, ...kebabNavFlags },
  });

  const container = document.getElementById('root');
  if (!container) throw new Error('No container found');
  const root = createRoot(container);
  root.render(
    <StrictMode>
      <LDProvider>
        <FeatureFlagContextProvider>
          <App />
        </FeatureFlagContextProvider>
      </LDProvider>
    </StrictMode>,
  );
};

const initializeApp = async () => {
  const whoamiResponse = getWhoami();
  const constantsResponse = getConstants();
  const accountResponse = listAccounts();
  try {
    const [whoami, constants, accounts] = await Promise.all([
      whoamiResponse,
      constantsResponse,
      accountResponse,
    ]);
    await maybeInitializeApp(constants, whoami, accounts);
  } catch (e) {
    if (
      axios.isAxiosError(e) &&
      (e.response?.status === 403 || e.response?.status === 401)
    ) {
      const constants = await constantsResponse;

      // If user is not authenticated, initialize app with unauthed routes only
      await maybeInitializeApp(constants);
    } else {
      throw e;
    }
  }
};

(async () => {
  await initializeApp();
})();
